// src/components/admin/SupportTicketHistory.jsx - Client ticket history view
import React, { useState, useEffect } from 'react';
import { useSalonAuth } from '../../contexts/SalonAuthContext';
import { getTicketsBySalonId, getTicketById, addTicketMessage } from '../../api/ticketService';

function SupportTicketHistory({ salonId, onBack }) {
  const { currentUser } = useSalonAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadTickets();
  }, [salonId]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const data = await getTicketsBySalonId(salonId);
      setTickets(data);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicket = async (ticketId) => {
    try {
      const ticket = await getTicketById(ticketId);
      setSelectedTicket(ticket);
      setReplyText('');
    } catch (error) {
      console.error('Error loading ticket detail:', error);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;

    try {
      setSending(true);
      await addTicketMessage(
        selectedTicket.id,
        replyText.trim(),
        'client',
        currentUser?.name || currentUser?.email || 'Client'
      );
      // Reload the ticket to show updated messages
      const updated = await getTicketById(selectedTicket.id);
      setSelectedTicket(updated);
      setReplyText('');
      // Also refresh the list for updated status
      loadTickets();
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleDateString('en-SG', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusClass = (status) => {
    if (status === 'Waiting for Support') return 'waiting-support';
    if (status === 'Waiting for Customer') return 'waiting-customer';
    return 'closed';
  };

  const getSeverityClass = (severity) => {
    if (severity === '1 - System Down') return 'system-down';
    if (severity === '2 - Significant Impact') return 'significant';
    if (severity === '3 - Minor Impact') return 'minor';
    return 'info';
  };

  // Detail view
  if (selectedTicket) {
    return (
      <div className="ticket-history-container">
        <div className="ticket-history-header">
          <h2>Ticket Details</h2>
          <button className="ticket-back-btn" onClick={() => setSelectedTicket(null)}>
            Back to Tickets
          </button>
        </div>

        <div className="ticket-detail-container">
          <div className="ticket-detail-header">
            <h3>{selectedTicket.ticketHeader}</h3>
            <span className={`ticket-status-badge ${getStatusClass(selectedTicket.ticketStatus)}`}>
              {selectedTicket.ticketStatus}
            </span>
          </div>

          <div className="ticket-detail-fields">
            <div className="ticket-detail-field">
              <label>Severity</label>
              <span className={`ticket-severity-badge ${getSeverityClass(selectedTicket.ticketSeverity)}`}>
                {selectedTicket.ticketSeverity}
              </span>
            </div>
            <div className="ticket-detail-field">
              <label>Type</label>
              <span>{selectedTicket.ticketType}</span>
            </div>
            <div className="ticket-detail-field">
              <label>Date Created</label>
              <span>{formatDate(selectedTicket.dateCreated)}</span>
            </div>
            <div className="ticket-detail-field">
              <label>Last Updated</label>
              <span>{formatDate(selectedTicket.dateUpdatedClient || selectedTicket.dateUpdatedSupport)}</span>
            </div>
          </div>

          <div className="ticket-detail-description">
            <h4>Description</h4>
            <p>{selectedTicket.ticketDescription}</p>
          </div>

          <div className="ticket-messages-section">
            <h4>Message Log</h4>
            <div className="ticket-messages">
              {(selectedTicket.messages || []).map((msg, idx) => (
                <div key={idx} className={`ticket-message ${msg.sender}`}>
                  <div className="ticket-message-sender">{msg.senderName}</div>
                  <div className="ticket-message-text">{msg.message}</div>
                  <div className="ticket-message-time">{formatDate(msg.timestamp)}</div>
                </div>
              ))}
            </div>

            {selectedTicket.ticketStatus !== 'Closed' && (
              <div className="ticket-reply-area">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your reply..."
                />
                <button
                  className="ticket-reply-btn"
                  onClick={handleSendReply}
                  disabled={sending || !replyText.trim()}
                >
                  {sending ? 'Sending...' : 'Send Reply'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="ticket-history-container">
      <div className="ticket-history-header">
        <h2>My Support Tickets</h2>
        <button className="ticket-back-btn" onClick={onBack}>
          Back to Modules
        </button>
      </div>

      {loading ? (
        <div className="ticket-loading">Loading tickets...</div>
      ) : tickets.length === 0 ? (
        <div className="ticket-empty-state">
          <p>No support tickets found.</p>
          <p>Submit a new ticket from the modules page if you need assistance.</p>
        </div>
      ) : (
        <table className="ticket-table">
          <thead>
            <tr>
              <th>Ticket Header</th>
              <th>Status</th>
              <th>Severity</th>
              <th>Type</th>
              <th>Date Created</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map(ticket => (
              <tr key={ticket.id}>
                <td>{ticket.ticketHeader}</td>
                <td>
                  <span className={`ticket-status-badge ${getStatusClass(ticket.ticketStatus)}`}>
                    {ticket.ticketStatus}
                  </span>
                </td>
                <td>
                  <span className={`ticket-severity-badge ${getSeverityClass(ticket.ticketSeverity)}`}>
                    {ticket.ticketSeverity}
                  </span>
                </td>
                <td>{ticket.ticketType}</td>
                <td>{formatDate(ticket.dateCreated)}</td>
                <td>
                  <button
                    className="ticket-view-btn"
                    onClick={() => handleViewTicket(ticket.id)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default SupportTicketHistory;
